import type {
  AgentRunStatus,
  ApprovalRequest,
  BackendArtifact,
  DevAgentActiveChangeset,
  DevAgentCoverageLines,
  DevAgentDependencySnapshot,
  DevAgentPendingFile,
  DevAgentPipelineStep,
  DevAgentVirtualFile,
  FeedItem,
  SessionCostData,
  TestRunEntry,
  TransparencyReport,
} from "./devAgentStore.types";

export interface DevAgentStoreData {
  runStatus: AgentRunStatus;
  sessionUuid: string | null;
  sseTicket: string | null;
  feedItems: FeedItem[];
  streamingContent: string | null;
  drawerOpen: boolean;
  drawerItem: FeedItem | null;
  pendingApproval: ApprovalRequest | null;
  suggestedActions: Array<{ label: string; query: string }>;
  transparencyReport: TransparencyReport | null;
  connectionStatus: "connected" | "reconnecting" | "disconnected";
  /** Salesforce OAuth usable for this session — SSE can set false on token errors; header refresh sets true. */
  sfConnected: boolean;
  sessionCost: SessionCostData;
  governorReport: {
    passed: boolean;
    limits: Array<{
      name: string;
      label: string;
      count: number;
      max: number;
      level: string;
    }>;
    critical_issues: string[];
  } | null;
  pipelineSteps: DevAgentPipelineStep[];
  pendingFile: DevAgentPendingFile | null;
  editorOpen: boolean;
  virtualFiles: Record<string, DevAgentVirtualFile>;
  acceptedCode: string | null;
  acceptedCodeTarget: string | null;
  conversationSummary: string[];
  dependencies: DevAgentDependencySnapshot[];
  artifactBadges: Record<string, "R" | "C" | "M">;
  artifactsById: Record<string, BackendArtifact>;
  coverageLines: DevAgentCoverageLines | null;
  testRuns: TestRunEntry[];
  runningTest: { testClassName: string; startedAt: number } | null;
  pinnedArtifactIds: Record<string, boolean>;
  dismissedPanels: Set<string>;
  activeChangeset: DevAgentActiveChangeset | null;
}

export type DevAgentSessionViewState = Pick<
  DevAgentStoreData,
  | "sessionUuid"
  | "sseTicket"
  | "feedItems"
  | "streamingContent"
  | "runStatus"
  | "connectionStatus"
  | "sfConnected"
  | "pipelineSteps"
  | "pendingApproval"
  | "suggestedActions"
  | "pinnedArtifactIds"
  | "artifactBadges"
  | "artifactsById"
  | "dependencies"
  | "testRuns"
  | "runningTest"
  | "conversationSummary"
  | "coverageLines"
  | "acceptedCode"
  | "acceptedCodeTarget"
  | "virtualFiles"
  | "pendingFile"
  | "activeChangeset"
  | "sessionCost"
  | "transparencyReport"
  | "governorReport"
  | "dismissedPanels"
>;

export function createInitialSessionCostData(): SessionCostData {
  return {
    totalCostUsd: 0,
    totalInputTokens: 0,
    totalOutputTokens: 0,
    requestCount: 0,
    lastRequestCostUsd: 0,
    cacheHitRate: 0,
  };
}

export function createDevAgentSessionViewState(
  sessionUuid: string | null,
  sseTicket: string | null = null,
): DevAgentSessionViewState {
  return {
    sessionUuid,
    sseTicket,
    feedItems: [],
    streamingContent: null,
    runStatus: "idle",
    connectionStatus: "disconnected",
    sfConnected: true,
    pipelineSteps: [],
    pendingApproval: null,
    suggestedActions: [],
    pinnedArtifactIds: {},
    artifactBadges: {},
    artifactsById: {},
    dependencies: [],
    testRuns: [],
    runningTest: null,
    conversationSummary: [],
    coverageLines: null,
    acceptedCode: null,
    acceptedCodeTarget: null,
    virtualFiles: {},
    pendingFile: null,
    activeChangeset: null,
    sessionCost: createInitialSessionCostData(),
    transparencyReport: null,
    governorReport: null,
    dismissedPanels: new Set<string>(),
  };
}

export function createInitialDevAgentStoreData(): DevAgentStoreData {
  return {
    runStatus: "idle",
    sessionUuid: null,
    sseTicket: null,
    feedItems: [],
    streamingContent: null,
    drawerOpen: false,
    drawerItem: null,
    pendingApproval: null,
    suggestedActions: [],
    transparencyReport: null,
    connectionStatus: "connected",
    sfConnected: true,
    sessionCost: createInitialSessionCostData(),
    governorReport: null,
    pipelineSteps: [],
    pendingFile: null,
    editorOpen: false,
    virtualFiles: {},
    acceptedCode: null,
    acceptedCodeTarget: null,
    conversationSummary: [],
    dependencies: [],
    artifactBadges: {},
    artifactsById: {},
    coverageLines: null,
    testRuns: [],
    runningTest: null,
    pinnedArtifactIds: {},
    dismissedPanels: new Set<string>(),
    activeChangeset: null,
  };
}
